import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { TwitterPicker } from 'react-color';

import { AccessTime } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import TagsAutocomplete from '../../../../../../../components/Autocompletes/Tags';
import { primary } from '../../../../../../../utils/brandColors';
import colors from '../../../../../../../utils/colors';

import styles from './styles';

const useStyles = makeStyles(styles);

export const GeneralFormComponent = (props) => {
  const classes = useStyles();
  const {
    eventTemplate,
    handleNameChange,
    handleChangeTags,
    handleDescriptionChange,
    isLoading,
    handleChangeEventDataDefaultValues,
    tags,
  } = props;

  if (isLoading) {
    return (
      <Card className={classes.card}>
        <CardHeader
          title="General Calendar Type Details"
          subheader="Define a calendar type by giving it a name, description, and optional tags."
        />
        <CardContent>
          <Skeleton height={56} sx={{ mb: 2 }} />
          <Skeleton height={56} sx={{ mb: 2 }} />
          <Skeleton height={56} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.card}>
      <CardHeader
        sx={{ mb: 2 }}
        title="General Calendar Type Details"
        subheader="Define a calendar type by giving it a name, description, and optional tags."
      />
      <CardContent>
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Stack spacing={2} flex={1}>
              <TextField
                error={!eventTemplate.validation.name.isValid}
                margin="normal"
                fullWidth
                required
                variant="outlined"
                id="name"
                label="Name"
                value={eventTemplate.name}
                autoFocus
                onChange={handleNameChange}
                helperText={
                  !eventTemplate.validation.name.isValid
                  && eventTemplate.validation.name.message
                }
              />

              <TextField
                error={!eventTemplate.validation.description.isValid}
                margin="normal"
                fullWidth
                variant="outlined"
                id="description"
                label="Description"
                value={eventTemplate.description}
                onChange={handleDescriptionChange}
                multiline
                minRows={3}
                helperText={
                  !eventTemplate.validation.description.isValid
                  && eventTemplate.validation.description.message
                }
              />
            </Stack>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Stack spacing={2} flex={1}>
              <FormControl margin="normal" fullWidth variant="outlined">
                <FormLabel id="board-display-radio-buttons-group-label">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box>Calendar Display Color</Box>
                    <Box
                      sx={{
                        ml: 1,
                        height: 24,
                        width: 24,
                        borderRadius: '50%',
                        border: '2px solid rgba(0,0,0,0.1)',
                        backgroundColor:
                          (eventTemplate.eventDataDefaultValues && eventTemplate.eventDataDefaultValues.calendarDisplayColor) || primary.main,
                      }}
                    />
                  </Box>
                </FormLabel>
                <TwitterPicker
                  colors={colors}
                  color={(eventTemplate.eventDataDefaultValues
                    && eventTemplate.eventDataDefaultValues.calendarDisplayColor) || primary.main}
                  onChange={(e) => {
                    const calendarDisplayColor = e.hex;
                    handleChangeEventDataDefaultValues({
                      ...eventTemplate.eventDataDefaultValues,
                      calendarDisplayColor,
                    });
                  }}
                  direction="vertical"
                />
                <Box
                  sx={{
                    mx: 2,
                    pb: 1,
                    fontSize: 14,
                    fontWeight: 300,
                  }}
                >
                  <Typography fontWeight={300} component="div">Need inspiration?</Typography>
                  <a href="https://g.co/kgs/rxPsQ5R" target="blank" rel="noreferrer">Open the Google hex color picker</a>
                </Box>
              </FormControl>
              <Stack spacing={1}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AccessTime color="primary" />
                  <Typography fontWeight={600}>Default Duration</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Set the default duration used when scheduling this calendar type.
                </Typography>
                <FormControl size="small" sx={{ maxWidth: 240 }}>
                  <InputLabel id="default-duration-label">Duration</InputLabel>
                  <Select
                    labelId="default-duration-label"
                    id="default-duration"
                    value={(eventTemplate.eventDataDefaultValues && eventTemplate.eventDataDefaultValues.defaultDuration
                      && parseInt(eventTemplate.eventDataDefaultValues.defaultDuration, 10)) || 1800000}
                    label="Duration"
                    onChange={(e) => {
                      handleChangeEventDataDefaultValues({
                        ...eventTemplate.eventDataDefaultValues,
                        defaultDuration: e.target.value,
                      });
                    }}
                  >
                    <MenuItem value="900000">15 minutes</MenuItem>
                    <MenuItem value="1800000">30 minutes</MenuItem>
                    <MenuItem value="2700000">45 minutes</MenuItem>
                    <MenuItem value="3600000">1 hour</MenuItem>
                    <MenuItem value="4500000">1 hour 15 minutes</MenuItem>
                    <MenuItem value="5400000">1 hour 30 minutes</MenuItem>
                    <MenuItem value="6300000">1 hour 45 minutes</MenuItem>
                    <MenuItem value="7200000">2 hours</MenuItem>
                    <MenuItem value="9000000">2 hours 30 minutes</MenuItem>
                    <MenuItem value="10800000">3 hours</MenuItem>
                    <MenuItem value="14400000">4 hours</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Grid>

        </Grid>

        <TagsAutocomplete
          onSelect={(e, value) => { handleChangeTags(value); }}
          value={tags}
          size="default"
        />
      </CardContent>
    </Card>
  );
};

GeneralFormComponent.propTypes = {
  eventTemplate: PropTypes.object,
  tags: PropTypes.array,
  handleNameChange: PropTypes.func,
  handleChangeTags: PropTypes.func,
  handleDescriptionChange: PropTypes.func,
  handleChangeEventDataDefaultValues: PropTypes.func,
  isLoading: PropTypes.bool,
};

GeneralFormComponent.defaultProps = {
  eventTemplate: {},
  tags: [],
  handleNameChange: () => { },
  handleChangeTags: () => { },
  handleDescriptionChange: () => { },
  handleChangeEventDataDefaultValues: () => { },
  isLoading: true,
};

export default GeneralFormComponent;
